import { createSelector } from "@reduxjs/toolkit";
import { isArray, isBoolean, isEqual, isString } from "lodash";
import { useSelector } from "react-redux";
import { useRequest } from "redux-query-react";
import { TenantModuleEnum, getUserSettingsRaw } from "api/ux/src";
import { appStore } from "index";
const tenantSettingsSelector = (state) => state.entities.UserSettings || {};
const getTenantConfig = () => tenantSettingsSelector(appStore.getState());
const modulesSources = {
    CFF: ["Forecasting"],
    ESG: ["RepRisk", "ESG"],
    INFRA: ["Insight Data"],
    PD: ["Insight Data"],
    PE: ["Insight Data"],
    RE: ["Insight Data"],
    RISK: ["Risk"],
    RISKBENCHMARKING: ["Risk Benchmarking"]
};
export const tenantModulesSelector = (state) => {
    const config = tenantSettingsSelector(state);
    return isArray(config.modules) ? config.modules : [];
};
export const tenantAladdinTickerPrefixSelector = () => {
    const config = getTenantConfig();
    if (isString(config.aladdinTickerPrefix)) {
        return config.aladdinTickerPrefix;
    }
    return "";
};
export const tenantSourcesSelector = createSelector(tenantModulesSelector, (tenantModules) => [].concat(...tenantModules.map((m) => modulesSources[m])));
export const isAdminEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.admin)) {
        return tenantConfig.admin;
    }
    return false;
};
export const isCffEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.cff)) {
        return tenantConfig.cff;
    }
    return false;
};
export const isInsightResearchEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.insightResearch)) {
        return tenantConfig.insightResearch;
    }
    return false;
};
export const isInsightAnalyticsEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.insightAnalytics)) {
        return tenantConfig.insightAnalytics;
    }
    return false;
};
export const isInsightResearchDownloadEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.insightResearchDownload)) {
        return tenantConfig.insightResearchDownload;
    }
    return false;
};
export const isPreqinShowConstituents = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.preqinShowConstituents)) {
        return tenantConfig.preqinShowConstituents;
    }
    return false;
};
export const isPreqinShowWhatsNewEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.preqinShowWhatsNewBanner)) {
        return tenantConfig.preqinShowWhatsNewBanner;
    }
    return false;
};
export const isPreqinClientEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.preqinClient)) {
        return tenantConfig.preqinClient;
    }
    return false;
};
export const isCommitmentPacingEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.commitmentPacing)) {
        return tenantConfig.commitmentPacing;
    }
    return false;
};
// Temporary until full decommission of CurrentGen
export const hidePreviousVersionLink = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.hidePreviousVersionLink)) {
        return tenantConfig.hidePreviousVersionLink;
    }
    return false;
};
export const areModulesEnabled = (modules) => {
    const tenantConfig = getTenantConfig();
    return modules.every((module) => tenantConfig.modules?.includes(TenantModuleEnum[module]));
};
export const isRiskModuleEnabled = () => {
    const tenantConfig = getTenantConfig();
    return isArray(tenantConfig.modules) && tenantConfig.modules?.includes(TenantModuleEnum.RISK);
};
export const isEsgModuleEnabled = () => {
    const tenantConfig = getTenantConfig();
    return isArray(tenantConfig.modules) && tenantConfig.modules?.includes(TenantModuleEnum.ESG);
};
export const isAnalyticsAssistantEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.analyticsCopilot)) {
        return tenantConfig.analyticsCopilot;
    }
    return false;
};
export const isAccessPreqinProEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig?.userActions?.accessPreqinPro)) {
        return Boolean(tenantConfig?.userActions?.accessPreqinPro);
    }
    return false;
};
export const isPermissionsEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig?.permissions)) {
        return Boolean(tenantConfig?.permissions);
    }
    return false;
};
export const haveOktaIdp = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig?.oktaMigrated)) {
        return Boolean(tenantConfig?.oktaMigrated);
    }
    return false;
};
export const useGetAllocationKey = () => {
    const tenantConfig = getTenantConfig();
    return tenantConfig.allocationKey;
};
export const useGetCommitmentCalculation = () => {
    const tenantConfig = getTenantConfig();
    return tenantConfig.commitmentCalculation;
};
export const useGetEsgSustainability = () => {
    const tenantConfig = getTenantConfig();
    return tenantConfig.esgSustainability;
};
const TenantSettingsSelector = (state) => state.entities.UserSettings;
export const useTenantSettings = (customQueryConfig) => {
    const [querystate, forceRefresh] = useRequest(getUserSettingsRaw({
        transform: (body) => ({ UserSettings: body }),
        ...customQueryConfig
    }));
    const tenantSettings = useSelector(TenantSettingsSelector, isEqual);
    return [
        tenantSettings,
        querystate,
        forceRefresh
    ];
};
export const isLightPortfolioAnalyticsEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig.lightPortfolioAnalytics)) {
        return Boolean(tenantConfig.lightPortfolioAnalytics);
    }
    return false;
};
export const isFundOpsModeEnabled = () => {
    const tenantConfig = getTenantConfig();
    if (isBoolean(tenantConfig?.useFundOpsModel)) {
        return Boolean(tenantConfig?.useFundOpsModel);
    }
    return false;
};
export const isVaultTenantIdConfigured = () => {
    const tenantConfig = getTenantConfig();
    return Boolean(tenantConfig.vaultTenantId);
};
